"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import DownloadResult from "./DownloadResult";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";

export default function ImageToPdfTool() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const { toolFetch, quotaError } = useToolFetch();

  const handleFilesSelected = useCallback((f: File[]) => {
    setFiles(f);
    setResult(null);
    setError("");
  }, []);

  const convert = useCallback(async () => {
    if (!files.length) return;
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));

      const res = await toolFetch("/api/tools/image-to-pdf", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to convert images");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to convert images");
    } finally {
      setProcessing(false);
    }
  }, [files]);

  const reset = useCallback(() => {
    setFiles([]);
    setResult(null);
    setError("");
  }, []);

  if (result) {
    return <DownloadResult fileName="images.pdf" fileSize={result.size} downloadUrl={result.url} onReset={reset} />;
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Converting images to PDF..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      <FileUploader
        accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
        multiple
        maxFiles={50}
        maxSizeMB={10}
        onFilesSelected={handleFilesSelected}
        label="Drop images here"
        description="JPG, PNG, or WebP — up to 50 images"
      />

      {files.length > 0 && (
        <>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button onClick={convert} disabled={processing} className="btn-primary w-full py-3">
            Convert {files.length} image{files.length !== 1 ? "s" : ""} to PDF
          </button>
        </>
      )}
    </div>
  );
}
